/**
 * Decision feed API client — merge decisions, pending reviews, learned patterns.
 *
 * Requests route through gateway (/api/v1/entity-decisions/*) which
 * proxies to entity_resolution_service /decisions/*.
 */

import { config } from '@/config/environment';
import { getAuthToken } from '@/contexts/AuthContext';
import axios from 'axios';

const decisionApi = axios.create({
  baseURL: config.GATEWAY_URL,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
});

decisionApi.interceptors.request.use((reqConfig) => {
  const token = getAuthToken();
  if (token) {
    reqConfig.headers.Authorization = `Bearer ${token}`;
  }
  return reqConfig;
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MergeDecisionSummary {
  id: string;
  entity_a_id: string;
  entity_b_id: string;
  entity_a_name: string | null;
  entity_b_name: string | null;
  entity_type: string;
  decision: 'merge' | 'separate' | 'pending_human';
  decided_by: 'cascade' | 'llm' | 'human' | 'learned_pattern' | 'system';
  composite_score: number;
  blast_radius: number;
  llm_judgment: string | null;
  llm_confidence: number | null;
  rationale: string;
  decided_at: string;
  reverted_at: string | null;
  // AW-22: number of pending rows in this inbox group. 1 = unique card,
  // >1 = representative; resolving fans out across all siblings.
  sibling_count: number;
  // Cross-type pair (e.g. customer + vendor sharing a domain label, or on a
  // pending card two records of different types outside the person family:
  // Marker group 28). Renders the dual-role question, never Merge; the decide
  // API refuses 'merge' (409) on a cross-type signal.
  cross_type: boolean;
  // AW-53: operator-pinned card — always ranked above the ambiguity
  // ordering in the Needs Your Take queue. Renders the Pinned badge.
  pinned?: boolean;
  // E12 ruled auto-resolve lane: a high-confidence same-type vendor/customer
  // fold Wingo executed under the elevated blast cap. Surfaced in the
  // Recent Decisions daily digest; revertible like any decision.
  auto_resolved?: boolean;
  // XC-ERP1-U6 R08(e): the judge's reasoning reduced server-side to one plain
  // sentence (internal ids stripped, at most 160 chars). Null when the row is
  // not the model's own call (a human/cascade/system successor — withheld by
  // design) or carries no usable reasoning; absent from a pre-C-06 server.
  // ReasoningLine decides which of those the card may call "not recorded".
  reasoning_line?: string | null;
  // Class judge-line-cites-signal-scores: the judge's line is in its prompt's
  // words (a side label, a model score, a signal name), not the owner's. ER may
  // still send the line (U1a) or send it null (U1c); the card draws neither and
  // never calls it not recorded. Absent from a server before U1a.
  reasoning_withheld?: boolean;
  /** Marker group 28 (AW-72): why the card was raised and what Merge / Keep Separate do, in plain words (server-built). */
  plain_reason?: string | null;
}

export interface WeeklyKpis {
  automated_count: number;
  pending_count: number;
  override_count: number;
  /** Null when the 30-day window has zero decisions — no data, not 100%. */
  accuracy_pct: number | null;
}

export interface DecisionFeedResponse {
  items: MergeDecisionSummary[];
  total: number;
  page: number;
  page_size: number;
  has_next: boolean;
  kpis: WeeklyKpis | null;
}

export interface PendingDecisionsResponse {
  items: MergeDecisionSummary[];
  total: number;
  page: number;
  page_size: number;
  has_next: boolean;
  /** Decisions this role may not see, left out of `items` and `total` (XC-WHO-SEES-WHAT W1d). */
  hidden_for_role?: number;
}

export interface LearnedPatternSummary {
  id: string;
  pattern_type: string;
  entity_type: string | null;
  description: string;
  decision: string;
  status: 'proposed' | 'confirmed' | 'rejected' | 'superseded';
  proposed_at: string;
  confirmed_at: string | null;
  estimated_pairs_resolved: number | null;
  actual_pairs_resolved: number;
}

export interface LearnedPatternsResponse {
  items: LearnedPatternSummary[];
  total: number;
  page: number;
  page_size: number;
  has_next: boolean;
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

export async function fetchDecisionFeed(params: {
  page?: number;
  page_size?: number;
  entity_type?: string;
  decided_by?: string;
  auto_resolved?: boolean;
}): Promise<DecisionFeedResponse> {
  const { data } = await decisionApi.get('/api/v1/entity-decisions/feed', { params });
  return data;
}

export async function fetchPendingDecisions(params: {
  page?: number;
  page_size?: number;
  entity_type?: string;
}): Promise<PendingDecisionsResponse> {
  const { data } = await decisionApi.get('/api/v1/entity-decisions/pending', { params });
  return data;
}

export async function fetchLearnedPatterns(params: {
  page?: number;
  page_size?: number;
  status?: string;
}): Promise<LearnedPatternsResponse> {
  const { data } = await decisionApi.get('/api/v1/entity-decisions/patterns', { params });
  return data;
}

export async function fetchDecisionKpis(): Promise<WeeklyKpis> {
  const { data } = await decisionApi.get('/api/v1/entity-decisions/kpis');
  return data;
}

export async function confirmDecision(decisionId: string): Promise<void> {
  await decisionApi.post(`/api/v1/entity-decisions/${decisionId}/confirm`);
}

// ---------------------------------------------------------------------------
// Decision context (Tier B) — source docs, field comparison, signal breakdown
// ---------------------------------------------------------------------------

export interface DecisionContextDocument {
  id: string;
  filename: string;
  page_count: number | null;
}

export interface FieldLocationBbox {
  left: number;
  top: number;
  right: number;
  bottom: number;
  page: number;
}

export interface FieldLocation {
  field_name: string;
  value: string;
  document_id: string;
  page: number | null;
  bbox: FieldLocationBbox | null;
  /** The spreadsheet cell the value was read from ("Sheet1!A35"); null when not read from a cell (AW-100). */
  cell?: string | null;
}

/** A place a document read a value: a page and its box, or a spreadsheet cell. */
export interface ValuePlace {
  page: number | null;
  bbox: FieldLocationBbox | null;
  cell: string | null;
}

/**
 * A value one document read for the side's record (AW-78): what the compare view lists under a
 * document it cannot draw, never the record's pooled `fields`.
 */
export interface DocumentValue {
  document_id: string;
  field_name: string;
  value: string;
  page: number | null;
  /** The spreadsheet cell it was read from ("Sheet1!A35"), when stored. */
  cell: string | null;
  /** Every place it was read that the screen can draw, in page order: a drawn document marks each (AW-78 residual). */
  places?: ValuePlace[];
  /** A value the record kept from the document, whose own reading is not linked: it may have read more. */
  from_record?: boolean;
}

export interface DecisionContextSide {
  name: string | null;
  source_system: string | null;
  source_id: string | null;
  documents: DecisionContextDocument[];
  fields: Record<string, string>;
  field_locations: FieldLocation[];
  /** What each of the side's documents read, one per (document, field, value) (AW-78). */
  document_values?: DocumentValue[];
}

export interface DecisionContext {
  decision_id: string;
  entity_type: string;
  entity_a: DecisionContextSide;
  entity_b: DecisionContextSide;
  relevant_fields: string[];
  signal_breakdown: Record<string, number>;
}

export async function fetchDecisionContext(decisionId: string): Promise<DecisionContext> {
  const { data } = await decisionApi.get(
    `/api/v1/entity-decisions/${decisionId}/context`,
  );
  return data;
}

export async function decidePending(
  decisionId: string,
  body: { decision: 'merge' | 'separate'; reason_code?: string; reason_text?: string },
): Promise<MergeDecisionSummary> {
  // AW-63: the click decides this card and plans its group's run; the
  // siblings apply in background tasks, so the client default (15 s) bounds it.
  const { data } = await decisionApi.post(
    `/api/v1/entity-decisions/pending/${decisionId}/decide`,
    body,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Override + Revert (DF8 backend, DF9 frontend)
// ---------------------------------------------------------------------------

export interface OverrideResponse {
  override_id: string;
  new_decision_id: string;
  saga_id: string;
}

export interface RevertResponse {
  revert_decision_id: string;
  predecessor_decision_id: string;
  saga_id: string;
  // AW-63 B6: the pairs this Undo reaches (1 for a single row; absent from an older engine).
  sibling_count?: number;
}

export async function overrideDecision(
  decisionId: string,
  body: {
    decision: 'merge' | 'separate';
    reason_code?: string;
    reason_text?: string;
    override_direction?: 'merge' | 'separate';
  },
): Promise<OverrideResponse> {
  const { data } = await decisionApi.post(
    `/api/v1/entity-decisions/${decisionId}/override`,
    body,
  );
  return data;
}

export async function revertDecision(
  decisionId: string,
  body: { reason: string },
): Promise<RevertResponse> {
  const { data } = await decisionApi.post(
    `/api/v1/entity-decisions/${decisionId}/revert`,
    body,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Pattern lifecycle (DF11)
// ---------------------------------------------------------------------------

export async function confirmPattern(patternId: string): Promise<LearnedPatternSummary> {
  const { data } = await decisionApi.post(
    `/api/v1/entity-decisions/patterns/${patternId}/confirm`,
  );
  return data;
}

export async function rejectPattern(
  patternId: string,
  body: { reason?: string },
): Promise<LearnedPatternSummary> {
  const { data } = await decisionApi.post(
    `/api/v1/entity-decisions/patterns/${patternId}/reject`,
    body,
  );
  return data;
}
